import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowUpRight, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUPPORT_WHATSAPP_NUMBER = "919036219589";

const TRACK_META = {
  data_cloud: {
    label: "Data Cloud",
    whatsappTag: "Data Cloud 30-Day Challenge",
    pillActive: "bg-indigo-500 text-white border-indigo-500",
    pillInactive: "border-white/10 text-slate-300 hover:border-indigo-400/50",
    accentBorder: "border-indigo-400/30",
    accentBg: "bg-indigo-400/10",
    button: "bg-indigo-500 hover:bg-indigo-600",
  },
  sfmc: {
    label: "SFMC",
    whatsappTag: "SFMC 30-Day Challenge",
    pillActive: "bg-teal-500 text-white border-teal-500",
    pillInactive: "border-white/10 text-slate-300 hover:border-teal-400/50",
    accentBorder: "border-teal-400/30",
    accentBg: "bg-teal-400/10",
    button: "bg-teal-500 hover:bg-teal-600",
  },
  mc_next: {
    label: "MC Next",
    whatsappTag: "MC Next 30-Day Challenge",
    pillActive: "bg-fuchsia-500 text-white border-fuchsia-500",
    pillInactive: "border-white/10 text-slate-300 hover:border-fuchsia-400/50",
    accentBorder: "border-fuchsia-400/30",
    accentBg: "bg-fuchsia-400/10",
    button: "bg-fuchsia-500 hover:bg-fuchsia-600",
  },
};

export default function TrainingInquiryModal({ open, onClose, defaultTrack = "data_cloud" }) {
  const { user } = useAuth();
  const [track, setTrack] = useState(defaultTrack);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const meta = TRACK_META[track];
  const whatsappMessage = `Hi! I'm interested in the ${meta.whatsappTag}. My name is ${name || "___"}. Please share more details on how to enroll.`;
  const whatsappLink = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;

  const handleClose = () => {
    setSubmitted(false);
    onClose();
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads/submit", {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        track,
        message: message.trim() || undefined,
        source: "challenge_page",
      });
      setSubmitted(true);
      toast.success("Request received! Our team will call you shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your request. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="training-inquiry-modal"
      onClick={handleClose}
    >
      <div
        className="glass-panel rounded-2xl p-6 sm:p-8 max-w-2xl w-full border border-white/10 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          data-testid="training-inquiry-modal-close-button"
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white mb-1">Talk to Us About Enrolling</h2>
        <p className="text-sm text-slate-400 mb-5">Tell us which track interests you — we'll help you enroll in the paid mentorship course.</p>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="training-inquiry-track-selector">
          {Object.entries(TRACK_META).map(([key, m]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTrack(key)}
              data-testid={`training-inquiry-track-${key}`}
              className={`text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${track === key ? m.pillActive : m.pillInactive}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={`rounded-xl border ${meta.accentBorder} ${meta.accentBg} p-5 flex flex-col`}>
            <p className="text-sm font-semibold text-white mb-1">Chat on WhatsApp</p>
            <p className="text-xs text-slate-400 mb-4 flex-1">Get instant answers about the {meta.label} training program from our advisor.</p>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              data-testid="training-inquiry-whatsapp-button"
              className="flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors"
            >
              Chat Now <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
            {submitted ? (
              <div className="flex flex-col items-center text-center py-4" data-testid="training-inquiry-form-success">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                <p className="text-sm font-semibold text-white">Request received!</p>
                <p className="text-xs text-slate-400 mt-1">Our team will call you shortly to help you enroll in the {meta.label} program.</p>
              </div>
            ) : (
              <form onSubmit={submitLead} className="space-y-2.5" data-testid="training-inquiry-form">
                <p className="text-sm font-semibold text-white mb-1">Request a Callback</p>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  data-testid="training-inquiry-name-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone Number"
                  data-testid="training-inquiry-phone-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional)"
                  data-testid="training-inquiry-email-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Your query (optional)"
                  rows={2}
                  data-testid="training-inquiry-message-input"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="training-inquiry-submit-button"
                  className={`w-full flex items-center justify-center gap-1.5 ${meta.button} disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors`}
                >
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Request Callback
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
